// Elv3FormDetails.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Button, Box, Typography } from "@mui/material";
import DeviceLocationSection from "./DeviceLocationSection";
import DeviceInfoDisplay from "./DeviceInfoDisplay";
import EmailLookupGrid from "./EmailLookupGrid";
import LocationInformation from "./LocationInformation";
import { locationFields } from "../../config/Elv3config";
import { useHeaders } from "../../HeadersContext";
import { fetchAllDOBProfiles } from "../../services/profileService"; // New import

export const Elv3FormDetails = ({ onFinalJSONGenerated }) => {
  const [formData, setFormData] = useState({
    privateResidence: {},
    elv3List: [],
    ApplicantInformation: {},
    OwnerInformation: {},
    locationInformation: {},
    PerformingAgencyDirectorInformation: {},
    PerformingAgencyInspectorInformation: {},
    PerformingMaintenanceDirectorInformation: {}, // New field added to state
    PerformingAgencyInspectorStatements: {},
    PerformingAgencyDirectorStatements: {},
  });
  const [locationInformation, setLocationInformation] = useState({});
  const [deviceData, setDeviceData] = useState(null);
  const { headers } = useHeaders();
  const [inspectionUserInput, setInspectionUserInput] = useState({});
  const [dobProfiles, setDobProfiles] = useState([]); // New state for DOB profiles
  const [devicesValidated, setDevicesValidated] = useState(false);

  const isPeriodicInspection = useMemo(() => {
    return (
      formData.elv3List &&
      formData.elv3List.some((item) => item.InspectionTypeID === "7")
    );
  }, [formData.elv3List]);

  // Callback from Elv3FormDevice:
  const handleDevicesValidated = (isValid) => {
    setDevicesValidated(isValid);
  };

  const allProfilesConfirmed =
    formData.ApplicantInformation?.Email &&
    formData.OwnerInformation?.Email &&
    formData.PerformingAgencyDirectorInformation?.Email &&
    formData.PerformingAgencyInspectorInformation?.Email &&
    (isPeriodicInspection
      ? formData.PerformingMaintenanceDirectorInformation?.Email
      : true);

  // Fetch DOB profiles only once when the parent mounts.
  useEffect(() => {
    const loadDobProfiles = async () => {
      try {
        const profiles = await fetchAllDOBProfiles();
        setDobProfiles(profiles || []);
      } catch (error) {
        console.error("Error fetching DOB profiles", error);
      }
    };
    loadDobProfiles();
  }, []);

  // Update formData.elv3List with all devices from deviceData.
  useEffect(() => {
    console.log("Device Data:", deviceData);
    if (Array.isArray(deviceData) && deviceData.length > 0) {
      setFormData((prev) => ({
        ...prev,
        elv3List: deviceData.map((device) => {
          const categoryProps =
            device.InspectionTypeID === "1"
              ? { IsCat1: true }
              : device.InspectionTypeID === "3" ||
                device.InspectionTypeID === "7"
              ? { IsCat5: true }
              : {};
          return {
            InspDate: device.InspDate || "",
            InspectionType: device.InspectionType || "",
            InspectionTypeID: device.InspectionTypeID || "",
            DeviceID: device.DeviceID || "",
            DeviceStatus: device.DeviceStatus || 1,
            DeviceTypeLabel: device.DeviceTypeLabel || "",
            InspectionDate: device.InspectionDate || "",
            DeviceGuid: device.DeviceGuid || "",
            ...categoryProps,
          };
        }),
      }));
    }
  }, [deviceData]);

  // Merge locationInformation into formData.locationInformation.
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      locationInformation: {
        ...prev.locationInformation,
        ...locationInformation,
      },
    }));
  }, [locationInformation]);

  const handleSectionInputChange = (sectionName, e, index = 0) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        [name]: value,
      },
    }));
  };

  // New generic callback when a profile is confirmed from DOBProfile.
  const handleProfileConfirmed = (targetSection, profileData) => {
    setFormData((prev) => ({
      ...prev,
      [targetSection]: {
        ...prev[targetSection],
        ...profileData,
      },
    }));
  };

  const handleSubmit = () => {
    const transformedElv3List = formData.elv3List.map((item) => {
      let deviceStatus = 1;
      let inspectionTypeID = "";
      let categoryProps = {};

      if (item.InspectionType === "1 Year Test / Category 1") {
        inspectionTypeID = "1";
        categoryProps = { IsCat1: true };
      } else if (item.InspectionType === "5 Year Test / Category 5") {
        inspectionTypeID = "3";
        categoryProps = { IsCat5: true };
      } else if (item.InspectionType === "Periodic Inspection") {
        inspectionTypeID = "7";
        categoryProps = { IsCat5: true };
      }

      return {
        ...item,
        InspDate: item.InspectionDate || "",
        InspectionTypeID: inspectionTypeID,
        DeviceStatus: deviceStatus,
        ...categoryProps,
      };
    });

    // Build the various sections from formData
    const applicantData = {
      ApplicantGuid: formData.ApplicantInformation.ApplicantGuid || "",
      BusinessAddress: headers.businessAddress || "",
      BusinessFax: formData.ApplicantInformation.BusinessFax || null,
      BusinessName: formData.ApplicantInformation.BusinessName || "",
      BusinessTelephne: formData.ApplicantInformation.BusinessTelephne || "",
      City: headers.city || "",
      Email: formData.ApplicantInformation.Email || "",
      FirstName: formData.ApplicantInformation.FirstName || "",
      FullName: formData.ApplicantInformation.FullName || "",
      LastName: formData.ApplicantInformation.LastName || "",
      MI: formData.ApplicantInformation.MI || null,
      Mobile: formData.ApplicantInformation.Mobile || "",
      State: headers.state || "",
      Zip: headers.zip || "",
    };

    const ownerData = {
      BusinessName: formData.OwnerInformation.BusinessName || "",
      BusinessPhone: formData.OwnerInformation.BusinessPhone || "",
      City: formData.OwnerInformation.City || "",
      ContactGuid: formData.OwnerInformation.ContactGuid || "",
      Email: formData.OwnerInformation.Email || "",
      FirstName: formData.OwnerInformation.FirstName || "",
      Fullname: formData.OwnerInformation.Fullname || null,
      LastName: formData.OwnerInformation.LastName || "",
      LicenseList: Array.isArray(formData.OwnerInformation.LicenseList)
        ? formData.OwnerInformation.LicenseList
        : [],
      MiddleName: formData.OwnerInformation.MiddleName || "",
      Mobile: formData.OwnerInformation.Mobile || "",
      OwnerType: formData.OwnerInformation.OwnerType || "1",
      State: formData.OwnerInformation.State || "",
      Street: formData.OwnerInformation.Street || "",
      Zip: formData.OwnerInformation.Zip || "",
    };

    const directorData = {
      BusinessName:
        formData.PerformingAgencyDirectorInformation.BusinessName || "",
      BusinessPhone:
        formData.PerformingAgencyDirectorInformation.BusinessPhone || "",
      City: formData.PerformingAgencyDirectorInformation.City || "",
      ContactGuid:
        formData.PerformingAgencyDirectorInformation.ContactGuid || "",
      Email: formData.PerformingAgencyDirectorInformation.Email || "",
      FirstName: formData.PerformingAgencyDirectorInformation.FirstName || "",
      Fullname: formData.PerformingAgencyDirectorInformation.Fullname || null,
      LastName: formData.PerformingAgencyDirectorInformation.LastName || "",
      LicenseList: Array.isArray(
        formData.PerformingAgencyDirectorInformation.LicenseList
      )
        ? formData.PerformingAgencyDirectorInformation.LicenseList
        : [],
      MiddleName: formData.PerformingAgencyDirectorInformation.MiddleName || "",
      Mobile: formData.PerformingAgencyDirectorInformation.Mobile || "",
      OwnerType: formData.PerformingAgencyDirectorInformation.OwnerType || null,
      State: formData.PerformingAgencyDirectorInformation.State || "",
      Street: formData.PerformingAgencyDirectorInformation.Street || "",
      Zip: formData.PerformingAgencyDirectorInformation.Zip || "",
      LicenseLookUpDetails:
        formData.PerformingAgencyDirectorInformation.LicenseLookUpDetails || {},
      CertificationNumber:
        formData.PerformingAgencyDirectorInformation.CertificationNumber || "",
      LicenseGuid:
        formData.PerformingAgencyDirectorInformation.LicenseGuid || "",
      LicenseNumber:
        formData.PerformingAgencyDirectorInformation.LicenseNumber || "",
      BadgeNumber:
        formData.PerformingAgencyDirectorInformation.BadgeNumber || null,
    };

    const inspectorData = {
      BusinessName:
        formData.PerformingAgencyInspectorInformation.BusinessName || "",
      BusinessPhone:
        formData.PerformingAgencyInspectorInformation.BusinessPhone || "",
      City: formData.PerformingAgencyInspectorInformation.City || "",
      ContactGuid:
        formData.PerformingAgencyInspectorInformation.ContactGuid || "",
      Email: formData.PerformingAgencyInspectorInformation.Email || "",
      FirstName: formData.PerformingAgencyInspectorInformation.FirstName || "",
      Fullname: formData.PerformingAgencyInspectorInformation.Fullname || null,
      LastName: formData.PerformingAgencyInspectorInformation.LastName || "",
      LicenseList: Array.isArray(
        formData.PerformingAgencyInspectorInformation.LicenseList
      )
        ? formData.PerformingAgencyInspectorInformation.LicenseList
        : [],
      MiddleName:
        formData.PerformingAgencyInspectorInformation.MiddleName || "",
      Mobile: formData.PerformingAgencyInspectorInformation.Mobile || "",
      OwnerType:
        formData.PerformingAgencyInspectorInformation.OwnerType || null,
      State: formData.PerformingAgencyInspectorInformation.State || "",
      Street: formData.PerformingAgencyInspectorInformation.Street || "",
      Zip: formData.PerformingAgencyInspectorInformation.Zip || "",
      LicenseLookUpDetails:
        formData.PerformingAgencyInspectorInformation.LicenseLookUpDetails ||
        {},
      CertificationNumber:
        formData.PerformingAgencyInspectorInformation.CertificationNumber || "",
      LicenseGuid:
        formData.PerformingAgencyInspectorInformation.LicenseGuid || "",
      LicenseNumber:
        formData.PerformingAgencyInspectorInformation.LicenseNumber || "",
    };

    // Conditionally include the new PerformingMaintenanceDirectorInformation
    let maintenanceDirectorData = {};
    // if (transformedElv3List.some((item) => item.InspectionTypeID === "7")) {
    //   maintenanceDirectorData =
    //     formData.PerformingMaintenanceDirectorInformation || {};
    // }
    if (
      transformedElv3List.some((item) => item.InspectionTypeID === "7") &&
      formData.PerformingMaintenanceDirectorInformation
    ) {
      // Destructure to remove unused fields and then spread the necessary fields
      const {
        CertificationNumber,
        LicenseNumber,
        BadgeNumber,
        ...filteredMaintenanceDirectorData
      } = formData.PerformingMaintenanceDirectorInformation;

      maintenanceDirectorData = filteredMaintenanceDirectorData;
    }

    const finalJSON = {
      IsItPrivateResidence: !!formData.privateResidence.IsItPrivateResidence,
      Elv3List: transformedElv3List,
      elv3GroupGuid: null,
      Witness: {},
      Inspecting: {},
      buildingUses: { BuildingOccupancyClassification: 1 },
      ApplicantInformation: applicantData,
      ReportType: "1",
      ReportYear: formData.ReportYear || null,
      LocationInformation: { ...formData.locationInformation },
      OwnerInformation: ownerData,
      PerformingAgencyDirectorInformation: directorData,
      PerformingAgencyInspectorInformation: inspectorData,
      PerformingAgencyInspectorStatementName:
        formData.PerformingAgencyInspectorStatements.name || "",
      PerformingAgencyInspectorStatementDate:
        formData.PerformingAgencyInspectorStatements.date || "",
      PerformingAgencyInspectorStatement:
        !!formData.PerformingAgencyInspectorStatements.statement,
      PerformingAgencyDirectorStatementName:
        formData.PerformingAgencyDirectorStatements.name || "",
      PerformingAgencyDirectorStatementDate:
        formData.PerformingAgencyDirectorStatements.date || "",
      PerformingAgencyDirectorStatement:
        !!formData.PerformingAgencyDirectorStatements.statement,
      PerformingAgencyDirectorStatementLicenseNumber:
        formData.PerformingAgencyDirectorStatements.licenseNumber || "",
      PerformingAgencyDirectorStatementAgencyName:
        formData.PerformingAgencyDirectorStatements.agencyName || "",
      ...(maintenanceDirectorData &&
        Object.keys(maintenanceDirectorData).length > 0 && {
          PerformingMaintenanceDirectorInformation: maintenanceDirectorData,
        }),
      IsSubmit: false,
      FinalAttestationCheckBox: false,
      FinalAttestationDate: null,
      FinalAttestationName: null,
    };

    console.log("Final JSON:", finalJSON);

    if (onFinalJSONGenerated) {
      onFinalJSONGenerated(finalJSON);
    }
  };

  return (
    <div className="p-4">
      <Box className="bg-white rounded-md shadow-md mb-4 p-4 w-full">
        <Box className="grid grid-cols-2 gap-4">
          <Box className="w-full">
            <DeviceLocationSection
              formData={formData}
              setFormData={setFormData}
              setLocationInformation={setLocationInformation}
              setDeviceData={setDeviceData}
              setInspectionUserInput={setInspectionUserInput}
              onDevicesValidated={handleDevicesValidated}
            />
          </Box>
          {Array.isArray(deviceData) && deviceData.length > 0 && (
            <Box className="w-full bg-white rounded-md p-4 border border-gray-300">
              {deviceData.map((device, index) => (
                <DeviceInfoDisplay key={index} deviceData={device} />
              ))}
              {Object.keys(locationInformation || {}).length > 0 && (
                <LocationInformation
                  title="Location Information"
                  locationFields={locationFields}
                  locationData={formData.locationInformation}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Render the new DOBProfile-based Email Lookup section */}
      {devicesValidated && (
        <Box mb={2} p={2} className="bg-white rounded-md shadow-md">
          <Typography variant="h6" gutterBottom>
            Email Lookup
          </Typography>
          <Box>
            <EmailLookupGrid
              formData={formData}
              handleSectionInputChange={handleSectionInputChange}
              handleProfileConfirmed={handleProfileConfirmed} // NEW callback only
              dobProfiles={dobProfiles} // Pass the pre-fetched DOB profiles
            />
          </Box>
        </Box>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!allProfilesConfirmed}
      >
        Save Form Data
      </Button>
    </div>
  );
};
